import React, { useEffect, useState } from 'react';
import { generateResolver, yup, VALIDATORS } from 'dyl-components';
import { Button, Form, Icon, List } from 'semantic-ui-react';
import { LocationUtils, PhoneUtil, RecordUtils, ValidationUtils } from 'utils';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import ConvertToCustomerFields from "./ConvertToCustomerFields";
import accountActions from 'actions/account';
import quoteActions from 'actions/quote';
import FullScreenModalPopUp from 'shared/FullScreenModalPopUp';
import PHONE_TYPE_OPTIONS from 'shared/constants/PHONE_TYPE_OPTIONS';
import EMAIL_TYPE_OPTIONS from 'shared/constants/EMAIL_TYPE_OPTIONS';
import RecordPhonesForm from './RecordPhonesForm';
import contactDuplicatesActions from 'actions/contact_duplicates';
import RecordEmailsForm from './RecordEmailsForm';
import { STATES } from 'shared/constants/STATES';
import useConvertToCustomer from './useConvertToCustomer';

import "./ConvertToCustomerForm.scss";
import campaignsActions from 'actions/campaigns';

const accountTypes = [
    { key: 'business', text: 'Business', value: 'business' },
    { key: 'household', text: 'Household', value: 'household' }
];

const ConvertToNewCustomerForm = ({
    onClose,
    size,
    onModify,
    pipeline_id,
    account_details = {},
    refresh,
    existing_account_id,
    owner_id,
    campaign_id
}) => {
    const { formState: { isDirty, isValid }, control, handleSubmit, setError } = useForm({
        mode: 'onChange',
        defaultValues: (() => {
            const { phone, phone_type, email, email_type, address } = account_details;
            const {
                label,
                street,
                additional_street,
                city,
                state,
                zip,
            } = address || {};
            return {
                name: '',
                account_type: '',
                pipeline: null,
                stage: null,
                business_model: null,
                phones: [{ value: phone || '', type: phone_type || PHONE_TYPE_OPTIONS.keys.CELL, main: true }],
                emails: [{ value: email || '', type: email_type || EMAIL_TYPE_OPTIONS.keys.WORK, main: true }],
                address_label: label,
                street: street || '',
                additional_street: additional_street || '',
                city: city || '',
                state: state || '',
                zip: zip || '',
                campaign_converted: campaign_id || undefined
            }
        })(),
        resolver: generateResolver({
            account_type: yup.string().required('This field is required'),
            name: VALIDATORS.BUSINESS_NAME().no_whitespace_only().required(),
            address_label: yup.string().required("This field is required"),
            state: yup.string().maxlength(100).minlength(2).required("This field is required"),
            street: yup.string().maxlength(100).minlength(2).required("This field is required"),
            additional_street: yup.string().maxlength(12).minlength(2),
            city: yup.string().maxlength(60).minlength(2).required("This field is required"),
            zip: VALIDATORS.US_POSTAL_CODE().required("This field is required"),
            phones: yup.array().of(yup.object().shape({
                value: VALIDATORS.PHONE_NUMBER().required('This field is required'),
                type: yup.string().required('This field is required')
            })).test('no_repeating_phone', "Phone already in the list", ValidationUtils.checkForDuplicatePhonesInTheList),
            emails: yup.array().of(yup.object().shape({
                value: VALIDATORS.EMAIL_ADDRESS().required('This field is required').maxlength(256),
                type: yup.string().required('This field is required')
            })).test('no_repeating_email', "Email already in the list", ValidationUtils.checkForDuplicateEmailsInTheList)
        })
    });

    useEffect(() => {
        onModify(isDirty);
    }, [isDirty, onModify]);

    const { isCreating, createCustomer, dispatch } = useConvertToCustomer({ onClose, refresh, pipeline_id });

    useEffect(() => {
        dispatch(campaignsActions.readPersonCampaignConverted({ person_id: owner_id, campaign_conversion: "customer" }));
    }, [dispatch, owner_id]);

    const linkContactsToAccount = async (account_id, owner_id) => {
        return dispatch(accountActions.linkContacts([account_id], [{
            person_id: Number(owner_id), primary_person: true, ...(existing_account_id ? { old_account_id: existing_account_id } : {})
        }]))
    }

    const onCreate = async (data) => {
        try {
            const { street, additional_street, city, state, zip, address_label, emails, phones } = data;
            const accounts = await dispatch(accountActions.createAccount([
                {
                    account_type: data.account_type,
                    name: data.name,
                    phone: phones.filter((phone) => phone.value).map(({ value, main, type }) => ({
                        main,
                        phone: PhoneUtil.getUnformatted(value),
                        phone_type: type
                    })),
                    location: [
                        {
                            street,
                            additional_street,
                            city,
                            state,
                            zip,
                            label: address_label
                        }
                    ],
                    email: emails.filter(emailItem => emailItem.value).map(({ value: email, main, type }) => ({
                        main,
                        email,
                        email_type: type
                    })),
                }
            ]));
            const account_id = Number(accounts[0].id);

            if (owner_id && account_id !== existing_account_id) {
                await linkContactsToAccount(account_id, owner_id);
            }

            if (owner_id) {
                await dispatch(accountActions.updateAccount(account_id, {
                    primary_person_id: Number(owner_id)
                }))
            }

            if (pipeline_id) {
                try {
                    await dispatch(quoteActions.updateQuoteAccountId(pipeline_id, { account_id }));
                } catch (e) {
                    console.log(e);
                }
            }

            await createCustomer({ account_id, owner_id, data });
            
        } catch (e) {
            console.log(e);
        }
    }

    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

    const { fields: phonesField, append: addPhone, remove: removePhone, update: updatePhone } = useFieldArray({
        control,
        name: 'phones'
    });

    const { fields: emailsField, append: addEmail, remove: removeEmail, update: updateEmail } = useFieldArray({
        control,
        name: 'emails'
    });

    const checkDuplicates = (queryParameters) => {
        return dispatch(contactDuplicatesActions.checkDuplicates(queryParameters));
    }

    return (
        <>
            <Form loading={isCreating} disabled={isCreating} size={size} noValidate className='ConvertToCustomerForm'>
                <Form.Group widths={2}>
                    <Controller
                        name='account_type'
                        control={control}
                        render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                            <Form.Select
                                fluid
                                label='Account Type'
                                name={name}
                                value={value}
                                onChange={(_, { value }) => { onChange({ target: { name, value } }) }}
                                options={accountTypes}
                                selectOnBlur={false}
                                placeholder='Select Type'
                                required
                                error={error && error.message && {
                                    content: error.message,
                                    pointing: 'below'
                                }}
                            />
                        )}
                    />
                    <Controller
                        name='name'
                        control={control}
                        render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                            <Form.Input
                                fluid
                                label='Account Name'
                                onChange={onChange}
                                name={name}
                                value={value}
                                placeholder="Type Account Name"
                                required
                                error={error?.message}
                            />
                        )}
                    />
                </Form.Group>
                <Form.Group widths={2}>
                    <Form.Field
                        label={(
                            <label>
                                <Icon link onClick={() => {
                                    addPhone({ value: '', type: PHONE_TYPE_OPTIONS.keys.CELL, main: false })
                                }} className='fas fa-plus-circle' color='primary' />
                                Phone <span className='AccountModal__asterisk'>*</span>
                            </label>
                        )}
                        control={List}
                    >
                        <RecordPhonesForm
                            control={control}
                            phones={phonesField}
                            onRemove={(index) => {
                                RecordUtils.removeItem(phonesField, index, updatePhone, removePhone);
                            }}
                            updateMain={(onChange) => {
                                RecordUtils.updateMain(phonesField, updatePhone, onChange);
                            }}
                            checkDuplicates={checkDuplicates}
                            contact_id={null}
                            setError={setError}
                        />
                    </Form.Field>
                    <Form.Field
                        label={(
                            <label>
                                <Icon link onClick={() => {
                                    addEmail({ value: '', type: EMAIL_TYPE_OPTIONS.keys.WORK, main: false })
                                }} className='fas fa-plus-circle' color='primary' />
                                Email <span className='AccountModal__asterisk'>*</span>
                            </label>
                        )}
                        control={List}
                    >
                        <RecordEmailsForm
                            control={control}
                            emails={emailsField}
                            onRemove={(index) => {
                                RecordUtils.removeItem(emailsField, index, updateEmail, removeEmail);
                            }}
                            updateMain={(onChange) => {
                                RecordUtils.updateMain(emailsField, updateEmail, onChange);
                            }}
                            checkDuplicates={checkDuplicates}
                            contact_id={null}
                            setError={setError}
                        />
                    </Form.Field>
                </Form.Group>
                <Form.Field
                    label='Address'
                    control={React.Fragment}
                    required
                >
                    <Form.Group widths='equal'>
                        <Controller
                            name='address_label'
                            control={control}
                            render={({ field: { name, onChange, value } }) => (
                                <Form.Select
                                    name={name}
                                    value={value}
                                    onChange={(_, { value }) => {
                                        onChange({ target: { name, value } })
                                    }}
                                    placeholder='Select Address Label'
                                    selectOnBlur={false}
                                    options={LocationUtils.getAddressLabelOptions()}
                                />
                            )}
                        />
                        <Controller
                            name='street'
                            control={control}
                            render={({ field: { name, onChange, value }, fieldState: { error } }) => (
                                <Form.Input
                                    name={name}
                                    value={value}
                                    onChange={(_, { value }) => {
                                        onChange({ target: { name, value } })
                                    }}
                                    placeholder='Street Address'
                                    error={error?.message}
                                />
                            )}
                        />
                    </Form.Group>
                </Form.Field>
                <Form.Group widths={4}>
                    <Controller
                        name='additional_street'
                        control={control}
                        render={({ field: { name, onChange, value }, fieldState: { error } }) => (
                            <Form.Input
                                name={name}
                                value={value}
                                onChange={(_, { value }) => {
                                    onChange({ target: { name, value } })
                                }}
                                placeholder='Apt, unit building, floor #'
                                error={error?.message}
                            />
                        )}
                    />
                    <Controller
                        name='city'
                        control={control}
                        render={({ field: { name, onChange, value }, fieldState: { error } }) => (
                            <Form.Input
                                name={name}
                                value={value}
                                onChange={(_, { value }) => {
                                    onChange({ target: { name, value } })
                                }}
                                placeholder='City'
                                error={error?.message}
                            />
                        )}
                    />
                    <Controller
                        name='state'
                        control={control}
                        render={({ field: { name, onChange, value }, fieldState: { error } }) => (
                            <Form.Select
                                name={name}
                                value={value}
                                onChange={(_, { value }) => {
                                    onChange({ target: { name, value } })
                                }}
                                placeholder='State'
                                search
                                options={STATES.map(state => ({ ...state, value: state.key }))}
                                error={error?.message}
                            />
                        )}
                    />
                    <Controller
                        name='zip'
                        control={control}
                        render={({ field: { name, onChange, value }, fieldState: { error } }) => (
                            <Form.Input
                                name={name}
                                value={value}
                                onChange={(_, { value }) => {
                                    onChange({ target: { name, value } })
                                }}
                                error={error?.message}
                                placeholder='Zipcode'
                            />
                        )}
                    />
                </Form.Group>
                <ConvertToCustomerFields control={control} columns={2} pipelineName={'pipeline'} stageName={'stage'} typeName={'business_model'} isEmpty={false} isCampaignConvertedDisabled={campaign_id} />
            </Form>
            <Button floated='right' disabled={!isValid || isCreating} primary onClick={() => setIsConfirmationOpen(true)} loading={isCreating}>Save</Button>
            <FullScreenModalPopUp
                header={'Are you sure?'}
                subheader={'Account type cannot be changed'}
                isOpen={isConfirmationOpen}
                onConfirm={handleSubmit(onCreate)}
                onFormClose={() => setIsConfirmationOpen(false)}
                closeIcon={false}
            />
        </>
    )
}

export default ConvertToNewCustomerForm;
